import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { createAuthenticatedClient, clearClientCache } from "@/hooks/useSupabaseClient";
import { normalizeLanguageCode } from "@/i18n/config";
import { useSimpleMode } from "./SimpleModeContext";
import { clearAllStores } from "@/stores";

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface User {
  id: string;
  phoneNumber: string;
  name: string;
  avatar?: string;
  role: "farmer" | "agent" | "admin";
  countryCode: string;
  // Currency for all money surfaces, single-sourced from the farmer's
  // countries row (currency_code + currency_symbol) via profiles.country_id.
  currencyCode?: string;
  currencySymbol?: string;
  language: string;
  biometricEnabled: boolean;
  createdAt: string;
  // Extended profile fields from DB
  region?: string;
  village?: string;
  farmSizeHectares?: number;
  farmingExperienceYears?: number;
  simpleMode?: boolean;
  isOnboarded?: boolean;
}

export interface AuthSession {
  accessToken: string; // This is our session token from verify_pin
  refreshToken?: string;
  expiresAt: number; // Unix timestamp
  userId: string;
}

export type AuthStatus = "idle" | "loading" | "authenticated" | "unauthenticated";

export type AuthEvent =
  | { type: "LOGIN_SUCCESS"; user: User }
  | { type: "LOGIN_FAILED"; error: string }
  | { type: "LOGOUT" }
  | { type: "SESSION_EXPIRED" }
  | { type: "SESSION_REFRESHED" }
  | { type: "BIOMETRIC_ENROLLED" }
  | { type: "PIN_CHANGED" }
  | { type: "PROFILE_UPDATED"; user: User };

type AuthEventListener = (event: AuthEvent) => void;

export interface LoginWithPinResult {
  success: boolean;
  locked?: boolean;
  lockedUntil?: string;
  attemptsRemaining?: number;
}

interface AuthContextType {
  // State
  user: User | null;
  session: AuthSession | null;
  status: AuthStatus;
  isLoading: boolean;
  isAuthenticated: boolean;

  // Auth methods
  loginWithPin: (
    phoneNumber: string,
    pin: string,
    rememberMe?: boolean,
    isoCountryCode?: string,
  ) => Promise<LoginWithPinResult>;
  loginWithBiometric: () => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;

  // PIN management
  verifyPin: (pin: string) => Promise<boolean>;
  changePin: (currentPin: string, newPin: string) => Promise<void>;
  resetPin: (phoneNumber: string, otp: string, newPin: string) => Promise<void>;

  // OTP
  requestOtp: (phoneNumber: string) => Promise<boolean>;
  verifyOtp: (phoneNumber: string, otp: string) => Promise<boolean>;

  // Biometric
  enrollBiometric: () => Promise<boolean>;
  isBiometricAvailable: boolean;

  // Session management
  refreshSession: () => Promise<void>;
  getAccessToken: () => string | null;
  getSessionToken: () => string | null;

  // Profile
  updateProfile: (data: Partial<User>) => Promise<void>;
  fetchProfile: () => Promise<User | null>;

  // Event system
  addEventListener: (listener: AuthEventListener) => () => void;
}

export interface RegisterData {
  phoneNumber: string;
  countryCode: string;
  name: string;
  pin: string;
  language?: string;
  clerkId?: string | null;
  /** INVITE-ONLY U4: ISO country code (e.g. 'BD', 'LA') — register_with_pin used to default to 'LA' (SD-T803). */
  countryIso?: string;
  /** INVITE-ONLY U4: the Clerk session the completed SMS step created; farmer-signup checks it with Clerk. */
  clerkSessionId?: string | null;
}

// ============================================================================
// Test Credentials (Fallback for dev)
// ============================================================================

const TEST_CREDENTIALS = {
  phoneNumber: "",
  pin: "1234",
  otp: "123456",
};

// ============================================================================
// Storage Keys
// ============================================================================

const STORAGE_KEYS = {
  session: "agxl_session",
  user: "agxl_user",
  biometric: "agxl_biometric_enrolled",
  rememberMe: "agxl_remember_me",
  lastPhone: "agxl_last_phone",
} as const;

// ============================================================================
// Context
// ============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// ============================================================================
// Helper: Map DB profile to User
// ============================================================================

interface DBProfile {
  id: string;
  full_name: string | null;
  phone: string | null;
  auth_phone: string | null;
  avatar_url: string | null;
  country_id: string | null;
  region: string | null;
  village: string | null;
  farm_size_hectares: number | null;
  farming_experience_years: number | null;
  language: string;
  simple_mode: boolean;
  is_onboarded: boolean;
  user_type: string;
  created_at: string;
}

const mapProfileToUser = (profile: DBProfile): User => ({
  id: profile.id,
  phoneNumber: profile.auth_phone || profile.phone || "",
  name: profile.full_name || "Farmer",
  avatar: profile.avatar_url || undefined,
  role: (profile.user_type as "farmer" | "agent" | "admin") || "farmer",
  countryCode: "BD", // Default, resolved via countries table lookup
  language: normalizeLanguageCode(profile.language) || "en",
  biometricEnabled: localStorage.getItem(STORAGE_KEYS.biometric) === "true",
  createdAt: profile.created_at,
  region: profile.region || undefined,
  village: profile.village || undefined,
  farmSizeHectares: profile.farm_size_hectares || undefined,
  farmingExperienceYears: profile.farming_experience_years || undefined,
  simpleMode: profile.simple_mode,
  isOnboarded: profile.is_onboarded,
});

// Resolve the farmer's country row. Currency (code + symbol) is the single
// source of truth for every money surface — no hardcoded maps or defaults.
const resolveCountry = async (
  countryId: string | null,
  client: typeof supabase,
): Promise<{ code: string | null; currencyCode: string | null; currencySymbol: string | null }> => {
  if (!countryId) return { code: null, currencyCode: null, currencySymbol: null };
  try {
    const { data } = await client
      .from("countries")
      .select("code, currency_code, currency_symbol")
      .eq("id", countryId)
      .single();
    return {
      code: data?.code ?? null,
      currencyCode: data?.currency_code ?? null,
      currencySymbol: data?.currency_symbol ?? null,
    };
  } catch {
    return { code: null, currencyCode: null, currencySymbol: null };
  }
};

// ============================================================================
// Provider
// ============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { isSimpleMode } = useSimpleMode();

  // State
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<AuthSession | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [isBiometricAvailable, setIsBiometricAvailable] = useState(false);
  const [eventListeners] = useState<Set<AuthEventListener>>(new Set());

  // Derived state
  const isLoading = status === "loading";
  const isAuthenticated = status === "authenticated" && !!user;

  // ============================================================================
  // Event System
  // ============================================================================

  const emitEvent = useCallback(
    (event: AuthEvent) => {
      eventListeners.forEach((listener) => {
        try {
          listener(event);
        } catch (e) {
          console.error("Auth event listener error:", e);
        }
      });
    },
    [eventListeners],
  );

  const addEventListener = useCallback(
    (listener: AuthEventListener) => {
      eventListeners.add(listener);
      return () => {
        eventListeners.delete(listener);
      };
    },
    [eventListeners],
  );

  // ============================================================================
  // Session Management
  // ============================================================================

  const persistSession = useCallback((sess: AuthSession, usr: User, remember: boolean) => {
    const storage = remember ? localStorage : sessionStorage;
    storage.setItem(STORAGE_KEYS.session, JSON.stringify(sess));
    storage.setItem(STORAGE_KEYS.user, JSON.stringify(usr));
    if (remember) {
      localStorage.setItem(STORAGE_KEYS.rememberMe, "true");
    }
  }, []);

  const clearSession = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.session);
    localStorage.removeItem(STORAGE_KEYS.user);
    localStorage.removeItem(STORAGE_KEYS.rememberMe);
    sessionStorage.removeItem(STORAGE_KEYS.session);
    sessionStorage.removeItem(STORAGE_KEYS.user);
    setSession(null);
    setUser(null);
    setStatus("unauthenticated");
  }, []);

  const getAccessToken = useCallback(() => {
    return session?.accessToken ?? null;
  }, [session]);

  const getSessionToken = useCallback(() => {
    // Alias for getAccessToken - the session token is used in x-session-token header
    return session?.accessToken ?? null;
  }, [session]);

  // ============================================================================
  // Fetch Profile from Supabase
  // ============================================================================

  const fetchProfile = useCallback(async (): Promise<User | null> => {
    if (!session?.accessToken || !session?.userId) {
      return null;
    }

    try {
      // Fetch profile by user ID
      const { data, error } = await supabase.from("profiles").select("*").eq("id", session.userId).single();

      if (error) {
        console.error("Failed to fetch profile:", error);
        return null;
      }

      if (data) {
        const dbProfile = data as unknown as DBProfile;
        const mappedUser = mapProfileToUser(dbProfile);

        // Priority: derive country CODE from phone number (most reliable for display).
        const phone = dbProfile.auth_phone || dbProfile.phone || "";
        const derivedFromPhone = deriveCountryCodeFromPhone(phone);
        // Currency is always sourced from the farmer's country_id (DB truth).
        const countryRow = await resolveCountry(dbProfile.country_id, supabase);
        if (derivedFromPhone) {
          mappedUser.countryCode = derivedFromPhone;
        } else if (countryRow.code) {
          mappedUser.countryCode = countryRow.code;
        }
        mappedUser.currencyCode = countryRow.currencyCode ?? undefined;
        mappedUser.currencySymbol = countryRow.currencySymbol ?? undefined;

        // Also update persisted user so session restore gets the right country
        const remember = localStorage.getItem(STORAGE_KEYS.rememberMe) === "true";
        if (session) {
          persistSession(session, mappedUser, remember);
        }

        setUser(mappedUser);
        return mappedUser;
      }

      return null;
    } catch (error) {
      console.error("Profile fetch error:", error);
      return null;
    }
  }, [session, persistSession]);

  // ============================================================================
  // Session Refresh
  // ============================================================================

  const refreshSession = useCallback(async () => {
    // For session-based auth, we verify the token is still valid
    if (!session?.accessToken) {
      throw new Error("No session token available");
    }

    // Check if session is still valid by attempting to fetch profile
    const profile = await fetchProfile();
    if (!profile) {
      clearSession();
      emitEvent({ type: "SESSION_EXPIRED" });
      throw new Error("Session expired");
    }

    emitEvent({ type: "SESSION_REFRESHED" });
  }, [session?.accessToken, fetchProfile, clearSession, emitEvent]);

  // ============================================================================
  // Initialize Auth State
  // ============================================================================

  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check both storages for existing session
        const storedSession =
          localStorage.getItem(STORAGE_KEYS.session) || sessionStorage.getItem(STORAGE_KEYS.session);
        const storedUser = localStorage.getItem(STORAGE_KEYS.user) || sessionStorage.getItem(STORAGE_KEYS.user);

        if (storedSession && storedUser) {
          const parsedSession = JSON.parse(storedSession) as AuthSession;
          const parsedUser = JSON.parse(storedUser) as User;

          // FIX: Correct countryCode from phone number BEFORE rendering.
          // localStorage may have stale 'BD'. deriveCountryCodeFromPhone is
          // a pure function — no DB, no async, no stale closure risk.
          const phoneForRestore = parsedUser.phoneNumber || "";
          const normalizedPhone = phoneForRestore.startsWith('+') ? phoneForRestore : `+${phoneForRestore}`;
          const dialToCountryRestore: Record<string, string> = {
            "+880": "BD",
            "+855": "KH",
            "+251": "ET",
            "+254": "KE",
            "+856": "LA",
            "+63": "PH",
            "+256": "UG",
            "+84": "VN",
            "+66": "TH",
          };
          const sortedRestore = Object.entries(dialToCountryRestore).sort((a, b) => b[0].length - a[0].length);
          for (const [dial, code] of sortedRestore) {
            if (normalizedPhone.startsWith(dial)) {
              parsedUser.countryCode = code;
              break;
            }
          }

          // Check if session expired
          if (parsedSession.expiresAt > Date.now()) {
            setSession(parsedSession);
            setUser(parsedUser);
            setStatus("authenticated");

            // Sync i18next language with restored user profile
            if (parsedUser.language) {
              const { default: i18n } = await import('@/i18n/config');
              i18n.changeLanguage(normalizeLanguageCode(parsedUser.language));
            }
          } else {
            // Session expired
            clearSession();
            emitEvent({ type: "SESSION_EXPIRED" });
          }
        } else {
          setStatus("unauthenticated");
        }

        // Check biometric availability
        if (window.PublicKeyCredential) {
          setIsBiometricAvailable(true);
        }
      } catch (error) {
        console.error("Auth init error:", error);
        clearSession();
      }
    };

    initAuth();
  }, []); // Only run once on mount

  // Session expiry check
  useEffect(() => {
    if (!session) return;

    const checkExpiry = () => {
      if (session.expiresAt <= Date.now()) {
        clearSession();
        emitEvent({ type: "SESSION_EXPIRED" });
      }
    };

    const interval = setInterval(checkExpiry, 60 * 1000); // Check every minute
    return () => clearInterval(interval);
  }, [session, clearSession, emitEvent]);

  // ============================================================================
  // Auth Methods
  // ============================================================================

  // Helper: derive country code from phone dial prefix
  const deriveCountryCodeFromPhone = (phone: string): string | null => {
    // Normalize: ensure phone starts with '+' for matching
    const normalized = phone.startsWith('+') ? phone : `+${phone}`;
    const dialToCountry: Record<string, string> = {
      "+880": "BD",
      "+855": "KH",
      "+251": "ET",
      "+254": "KE",
      "+856": "LA",
      "+63": "PH",
      "+256": "UG",
      "+84": "VN",
      "+66": "TH",
    };
    // Sort by longest prefix first to avoid "+25" matching "+256" before "+251"
    const sorted = Object.entries(dialToCountry).sort((a, b) => b[0].length - a[0].length);
    for (const [dial, code] of sorted) {
      if (normalized.startsWith(dial)) return code;
    }
    return null;
  };

  const loginWithPin = useCallback(
    async (
      phoneNumber: string,
      pin: string,
      rememberMe = false,
      isoCountryCode?: string,
    ): Promise<LoginWithPinResult> => {
      setStatus("loading");

      try {
        // Clean phone number for lookup
        const cleanPhone = phoneNumber.replace(/\s/g, "");

        // Step 1: Check rate limit first
        const { data: rateCheck, error: rateError } = await supabase.rpc("check_pin_rate_limit", {
          phone_input: cleanPhone,
        });

        if (rateError) {
          console.error("Rate limit check error:", rateError);
          setStatus("unauthenticated");
          return { success: false };
        }

        const rateResult = rateCheck as Array<{
          is_allowed: boolean;
          attempts_remaining: number;
          lockout_until: string | null;
        }>;
        if (rateResult && rateResult.length > 0 && !rateResult[0].is_allowed) {
          const lockoutUntil = rateResult[0].lockout_until ?? undefined;
          setStatus("unauthenticated");
          emitEvent({ type: "LOGIN_FAILED", error: "Account locked due to rate limit" });
          return { success: false, locked: true, lockedUntil: lockoutUntil };
        }

        // Step 2: Verify PIN securely
        const { data, error } = await supabase.rpc("verify_pin_secure", {
          phone_input: cleanPhone,
          pin_input: pin,
        });

        if (error) {
          console.error("Verify PIN error:", error);
          setStatus("unauthenticated");
          return { success: false };
        }

        // verify_pin_secure returns: { user_id, is_valid, session_token, error_code }
        // On failure, session_token carries 'LOCKED:<timestamp>' or 'ATTEMPTS:<n>' (C-05).
        const result = data as Array<{ is_valid: boolean; user_id: string; session_token: string }> | null;

        if (!result || result.length === 0 || !result[0].is_valid) {
          setStatus("unauthenticated");
          emitEvent({ type: "LOGIN_FAILED", error: "Invalid phone number or PIN" });

          if (data && data.length > 0 && !data[0].is_valid) {
            const token = data[0].session_token as string | null;
            if (token && token.startsWith('LOCKED:')) {
              return { success: false, locked: true, lockedUntil: token.replace('LOCKED:', '') };
            }
            if (token && token.startsWith('ATTEMPTS:')) {
              const remaining = parseInt(token.replace('ATTEMPTS:', ''), 10);
              return {
                success: false,
                locked: false,
                attemptsRemaining: isNaN(remaining) ? undefined : remaining,
              };
            }
          }
          return { success: false };
        }

        const { user_id, session_token } = result[0];

        // Cross-user login guard: if the previously-persisted user differs
        // from the one logging in, drop all per-user Zustand state so we
        // don't render the prior user's batch/score/records.
        try {
          const priorRaw =
            localStorage.getItem(STORAGE_KEYS.user) ||
            sessionStorage.getItem(STORAGE_KEYS.user);
          if (priorRaw) {
            const priorUser = JSON.parse(priorRaw) as { id?: string };
            if (priorUser?.id && priorUser.id !== user_id) {
              clearAllStores();
            }
          }
        } catch {
          // malformed persisted user — safest to purge
          clearAllStores();
        }

        // Create session
        const newSession: AuthSession = {
          accessToken: session_token,
          expiresAt: Date.now() + 30 * 24 * 60 * 60 * 1000, // 30 days
          userId: user_id,
        };

        // Set session first so we can fetch profile
        setSession(newSession);

        // Fetch profile from database using authenticated client with session token
        const authClient = createAuthenticatedClient(session_token);
        const { data: profileData, error: profileError } = await authClient
          .from("profiles")
          .select("*")
          .eq("id", user_id)
          .single();

        if (profileError) {
          console.error("Profile fetch error:", profileError);
          throw new Error("Failed to load profile");
        }

        const dbProfile = profileData as unknown as DBProfile;
        const mappedUser = mapProfileToUser(dbProfile);

        // Country code: use explicit ISO code passed from login screen (ground truth).
        // Falls back to phone prefix parsing, then keeps default 'BD'.
        const derivedCountry = isoCountryCode || deriveCountryCodeFromPhone(cleanPhone);
        if (derivedCountry) {
          mappedUser.countryCode = derivedCountry;
        }

        // Currency is sourced from the farmer's country_id (single source of
        // truth) and persisted on the user so session restore keeps it.
        const countryRow = await resolveCountry(dbProfile.country_id, supabase);
        mappedUser.currencyCode = countryRow.currencyCode ?? undefined;
        mappedUser.currencySymbol = countryRow.currencySymbol ?? undefined;

        // Persist session — countryCode is now correct and will survive session restore
        persistSession(newSession, mappedUser, rememberMe);
        localStorage.setItem(STORAGE_KEYS.lastPhone, cleanPhone);

        setUser(mappedUser);
        setStatus("authenticated");

        // Sync i18next language with the user's profile language
        if (mappedUser.language) {
          const { default: i18n } = await import('@/i18n/config');
          i18n.changeLanguage(normalizeLanguageCode(mappedUser.language));
        }

        emitEvent({ type: "LOGIN_SUCCESS", user: mappedUser });
        return { success: true };
      } catch (error) {
        setStatus("unauthenticated");
        const message = error instanceof Error ? error.message : "Login failed";
        emitEvent({ type: "LOGIN_FAILED", error: message });
        return { success: false };
      }
    },
    [persistSession, emitEvent],
  );

  const loginWithBiometric = useCallback(async () => {
    const enrolled = localStorage.getItem(STORAGE_KEYS.biometric) === "true";
    if (!enrolled) {
      throw new Error("Biometric not enrolled");
    }

    setStatus("loading");

    try {
      // Get stored session/user for biometric login
      const storedSession = localStorage.getItem(STORAGE_KEYS.session);
      const storedUser = localStorage.getItem(STORAGE_KEYS.user);

      if (!storedSession || !storedUser) {
        throw new Error("No stored credentials for biometric login");
      }

      const parsedSession = JSON.parse(storedSession) as AuthSession;
      const parsedUser = JSON.parse(storedUser) as User;

      // FIX: Correct countryCode from phone before using stored user
      const phoneForBio = parsedUser.phoneNumber || "";
      const dialToCountryBio: Record<string, string> = {
        "+880": "BD",
        "+855": "KH",
        "+251": "ET",
        "+254": "KE",
        "+856": "LA",
        "+63": "PH",
        "+256": "UG",
        "+84": "VN",
        "+66": "TH",
      };
      for (const [dial, code] of Object.entries(dialToCountryBio)) {
        if (phoneForBio.startsWith(dial)) {
          parsedUser.countryCode = code;
          break;
        }
      }

      // Verify session is still valid
      if (parsedSession.expiresAt <= Date.now()) {
        throw new Error("Session expired, please login with PIN");
      }

      // Simulate biometric verification delay
      await new Promise((resolve) => setTimeout(resolve, 500));

      setSession(parsedSession);
      setUser(parsedUser);
      setStatus("authenticated");

      // Sync i18next language with restored user profile
      if (parsedUser.language) {
        const { default: i18n } = await import('@/i18n/config');
        i18n.changeLanguage(normalizeLanguageCode(parsedUser.language));
      }

      emitEvent({ type: "LOGIN_SUCCESS", user: parsedUser });
    } catch (error) {
      setStatus("unauthenticated");
      throw error;
    }
  }, [emitEvent]);

  const register = useCallback(
    async (data: RegisterData) => {
      setStatus("loading");

      try {
        const cleanPhone = data.phoneNumber.replace(/\s/g, "");
        const fullPhone = `${data.countryCode}${cleanPhone}`;

        // INVITE-ONLY U4 (Farmer_Visibility §6.2, §6.11): the ONE server sign-up step, shared with CCS. It checks the
        // country switch, that an organisation has added this number, and the SMS step WITH CLERK, then creates the
        // farmer (register_with_pin is closed to the public key). The country is sent explicitly (SD-T803).
        const { data: result, error } = await supabase.functions.invoke('farmer-signup', {
          body: {
            action: 'register',
            phone: fullPhone,
            pin: data.pin,
            country_code: data.countryIso,
            clerk_user_id: data.clerkId ?? null,
            clerk_session_id: data.clerkSessionId ?? null,
            farm_type: 'poultry',
          },
        });
        if (error) throw Object.assign(new Error('REGISTRATION_FAILED'), { code: 'REGISTRATION_FAILED' });
        if (!result?.success) {
          const code = String(result?.error_code ?? 'REGISTRATION_FAILED');
          throw Object.assign(new Error(code), { code });
        }
        const row = { user_id: result.user_id as string | null };

        // Establish session first
        const loginResult = await loginWithPin(fullPhone, data.pin, true);
        if (!loginResult.success) {
          throw new Error('Registration succeeded but auto-login failed. Please log in manually.');
        }

        // Update full_name after session is active (RLS requires active session)
        if (data.name && row.user_id) {
          await supabase
            .from('profiles')
            .update({ full_name: data.name })
            .eq('id', row.user_id);
        }

      } catch (error) {
        setStatus("unauthenticated");
        throw error;
      }
    },
    [loginWithPin],
  );

  const logout = useCallback(async () => {
    // Play goodbye greeting before clearing session
    try {
      const storedUser = localStorage.getItem(STORAGE_KEYS.user);
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        const countryCode = parsed?.countryCode?.toUpperCase();
        const GOODBYE_AUDIO: Record<string, string> = {
          LA: "/audio/greetings/la_kon_lao_goodbye.mp3",
        };
        const src = GOODBYE_AUDIO[countryCode];
        if (src) {
          const audio = new Audio(src);
          audio.volume = 0.8;
          audio.play().catch(() => {});
        }
      }
    } catch {
      /* ignore */
    }

    try {
      // Deactivate session in database if we have a token
      if (session?.accessToken) {
        await supabase.from("sessions").update({ is_active: false }).eq("token", session.accessToken);
      }
    } catch (error) {
      console.error("Logout error:", error);
    }

    clearClientCache();
    clearSession();
    clearAllStores();
    emitEvent({ type: "LOGOUT" });
  }, [session, clearSession, emitEvent]);

  // ============================================================================
  // PIN Management
  // ============================================================================

  const verifyPin = useCallback(
    async (pin: string): Promise<boolean> => {
      if (!user?.phoneNumber) return false;

      try {
        // Check rate limit first
        const { data: rateCheck } = await supabase.rpc("check_pin_rate_limit", {
          phone_input: user.phoneNumber,
        });

        const rateResult = rateCheck as Array<{
          is_allowed: boolean;
          attempts_remaining: number;
          lockout_until: string | null;
        }>;
        if (rateResult && rateResult.length > 0 && !rateResult[0].is_allowed) {
          return false;
        }

        // Verify PIN securely
        const { data, error } = await supabase.rpc("verify_pin_secure", {
          phone_input: user.phoneNumber,
          pin_input: pin,
        });

        if (error) return false;

        const result = data as Array<{ is_valid: boolean }> | null;
        return result != null && result.length > 0 && result[0].is_valid;
      } catch {
        return false;
      }
    },
    [user?.phoneNumber],
  );

  const changePin = useCallback(
    async (currentPin: string, newPin: string) => {
      const isValid = await verifyPin(currentPin);
      if (!isValid) {
        throw new Error("Current PIN is incorrect");
      }

      if (newPin.length !== 4) {
        throw new Error("New PIN must be 4 digits");
      }

      if (!user?.id) {
        throw new Error("Not authenticated");
      }

      // Update PIN in database
      const { error } = await supabase
        .from("profiles")
        .update({ pin_hash: newPin }) // In production, hash this server-side
        .eq("id", user.id);

      if (error) {
        throw new Error("Failed to update PIN");
      }

      emitEvent({ type: "PIN_CHANGED" });
    },
    [verifyPin, user?.id, emitEvent],
  );

  const resetPin = useCallback(
    async (phoneNumber: string, otp: string, newPin: string) => {
      // Verify OTP first
      const isValidOtp = await verifyOtp(phoneNumber, otp);
      if (!isValidOtp) {
        throw new Error("Invalid OTP");
      }

      if (newPin.length !== 4) {
        throw new Error("New PIN must be 4 digits");
      }

      // Update PIN in database
      const { error } = await supabase.from("profiles").update({ pin_hash: newPin }).eq("auth_phone", phoneNumber);

      if (error) {
        throw new Error("Failed to reset PIN");
      }

      emitEvent({ type: "PIN_CHANGED" });
    },
    [emitEvent],
  );

  // ============================================================================
  // OTP
  // ============================================================================

  const requestOtp = useCallback(async (phoneNumber: string): Promise<boolean> => {
    // In production, this would call an edge function to send OTP via SMS
    const cleanPhone = phoneNumber.replace(/\D/g, "");
    if (cleanPhone.length < 8) {
      return false;
    }

    return true;
  }, []);

  const verifyOtp = useCallback(async (_phoneNumber: string, otp: string): Promise<boolean> => {
    // DEV-ONLY: Accept any 6-digit OTP for testing (remove before production)
    return otp === TEST_CREDENTIALS.otp || otp.length === 6;
  }, []);

  // ============================================================================
  // Biometric
  // ============================================================================

  const enrollBiometric = useCallback(async (): Promise<boolean> => {
    if (!isBiometricAvailable) {
      return false;
    }

    // Simulate biometric enrollment
    await new Promise((resolve) => setTimeout(resolve, 1000));

    localStorage.setItem(STORAGE_KEYS.biometric, "true");

    if (user) {
      const updatedUser = { ...user, biometricEnabled: true };
      setUser(updatedUser);
      const remember = localStorage.getItem(STORAGE_KEYS.rememberMe) === "true";
      if (session) {
        persistSession(session, updatedUser, remember);
      }
    }

    emitEvent({ type: "BIOMETRIC_ENROLLED" });
    return true;
  }, [isBiometricAvailable, user, session, persistSession, emitEvent]);

  // ============================================================================
  // Profile Update
  // ============================================================================

  const updateProfile = useCallback(
    async (data: Partial<User>) => {
      if (!user || !session) {
        throw new Error("Not authenticated");
      }

      // Map User fields to DB column names
      const dbUpdate: Record<string, unknown> = {};
      if (data.name !== undefined) dbUpdate.full_name = data.name;
      if (data.avatar !== undefined) dbUpdate.avatar_url = data.avatar;
      if (data.region !== undefined) dbUpdate.region = data.region;
      if (data.village !== undefined) dbUpdate.village = data.village;
      if (data.farmSizeHectares !== undefined) dbUpdate.farm_size_hectares = data.farmSizeHectares;
      if (data.farmingExperienceYears !== undefined) dbUpdate.farming_experience_years = data.farmingExperienceYears;
      if (data.language !== undefined) dbUpdate.language = data.language;
      if (data.simpleMode !== undefined) dbUpdate.simple_mode = data.simpleMode;

      const { error } = await supabase.from("profiles").update(dbUpdate).eq("id", user.id);

      if (error) {
        console.error("Profile update error:", error);
        throw new Error("Failed to update profile");
      }

      const updatedUser = { ...user, ...data };
      setUser(updatedUser);

      const remember = localStorage.getItem(STORAGE_KEYS.rememberMe) === "true";
      persistSession(session, updatedUser, remember);

      emitEvent({ type: "PROFILE_UPDATED", user: updatedUser });
    },
    [user, session, persistSession, emitEvent],
  );

  // ============================================================================
  // Context Value
  // ============================================================================

  const value: AuthContextType = {
    // State
    user,
    session,
    status,
    isLoading,
    isAuthenticated,

    // Auth methods
    loginWithPin,
    loginWithBiometric,
    register,
    logout,

    // PIN management
    verifyPin,
    changePin,
    resetPin,

    // OTP
    requestOtp,
    verifyOtp,

    // Biometric
    enrollBiometric,
    isBiometricAvailable,

    // Session
    refreshSession,
    getAccessToken,
    getSessionToken,

    // Profile
    updateProfile,
    fetchProfile,

    // Events
    addEventListener,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
